import React, { useState, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Badge,
  ButtonGroup,
  Modal,
  Spinner,
} from "react-bootstrap";

import axios from "axios";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Customization.css";
import AppContext from "../../../../context/AppContext";
/* ---------------------------------------------------------
   Persisted-state keys (refresh ke baad wapis load karne ke liye)
--------------------------------------------------------- */

const PERSIST_KEY = "customizationState";

const loadPersistedState = () => {
  try {
    const raw = sessionStorage.getItem(PERSIST_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const savePersistedState = (data) => {
  try {
    sessionStorage.setItem(PERSIST_KEY, JSON.stringify(data));
  } catch (err) {
    console.error("Customization state save failed:", err);
  }
};

/* ---------------------------------------------------------
   Common download helper (image URL -> device download)
--------------------------------------------------------- */

const downloadImageFromUrl = async (imageUrl, filenamePrefix = "image") => {
  if (!imageUrl) return;
  try {
    const response = await fetch(imageUrl, { mode: "cors" });
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const ext = blob.type.includes("png") ? "png" : "jpg";
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `${filenamePrefix}_${Date.now()}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error("Download failed:", err);
    // Fallback: naya tab open kar do agar fetch/CORS block ho
    window.open(imageUrl, "_blank");
  }
};

/* ---------------------------------------------------------
   Lightweight inline icon set
--------------------------------------------------------- */

const Icon = ({ name, size = 18 }) => {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };

  switch (name) {
    case "menu":
      return (
        <svg {...common}>
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      );

    case "back":
      return (
        <svg {...common}>
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
      );

    case "history":
      return (
        <svg {...common}>
          <path d="M3 12a9 9 0 1 0 3-6.7" />
          <path d="M3 4v5h5" />
          <path d="M12 7v5l3 3" />
        </svg>
      );

    case "bell":
      return (
        <svg {...common}>
          <path d="M6 8a6 6 0 1 1 12 0c0 4 1.5 5.5 1.5 5.5H4.5S6 12 6 8Z" />
          <path d="M10 19a2 2 0 0 0 4 0" />
        </svg>
      );

    case "silhouette":
      return (
        <svg {...common}>
          <path d="M12 3c2 2 2 4 0 6-2-2-2-4 0-6Z" />
          <path d="M8 21c0-6 1.5-9 4-9s4 3 4 9" />
        </svg>
      );

    case "neckline":
      return (
        <svg {...common}>
          <path d="M4 20c2-6 5-9 8-9s6 3 8 9" />
          <path d="M9 11c1 2 5 2 6 0" />
        </svg>
      );

    case "sleeve":
      return (
        <svg {...common}>
          <path d="M4 6c3-2 13-2 16 0l-2 6-3-2v10H9V10L6 12Z" />
        </svg>
      );

    case "trouser":
      return (
        <svg {...common}>
          <path d="M6 3h12l1 18h-4l-1-11-1 11H9L8 3Z" />
        </svg>
      );

    case "vneck":
      return (
        <svg {...common}>
          <path d="M5 4h14l-2 6-5 4-5-4Z" />
        </svg>
      );

    case "crown":
      return (
        <svg {...common}>
          <path d="M4 18h16l-1-8-4 3-3-6-3 6-4-3Z" />
        </svg>
      );

    case "round":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="7" />
        </svg>
      );

    case "plus":
      return (
        <svg {...common}>
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      );

    case "straight":
      return (
        <svg {...common}>
          <line x1="4" y1="4" x2="4" y2="20" />
          <line x1="20" y1="4" x2="20" y2="20" />
        </svg>
      );

    case "salwar":
      return (
        <svg {...common}>
          <path d="M12 3c3 3 6 4 6 8 0 4-3 4-3 4s1 4-3 6c-4-2-3-6-3-6s-3 0-3-4c0-4 3-5 6-8Z" />
        </svg>
      );

    case "tulip":
      return (
        <svg {...common}>
          <path d="M6 20c0-6 2-8 6-8s6 2 6 8" />
          <path d="M12 12V4" />
        </svg>
      );

    case "sparkle":
      return (
        <svg {...common}>
          <path d="M12 3l1.6 4.8L18 9.4l-4.4 1.6L12 16l-1.6-5L6 9.4l4.4-1.6Z" />
        </svg>
      );

    case "check":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M8 12l3 3 5-6" />
        </svg>
      );

    case "sleeveless":
      return (
        <svg {...common}>
          <path d="M8 4h8l1 6-3-1v11H10V9L7 10Z" />
        </svg>
      );

    case "halfsleeve":
      return (
        <svg {...common}>
          <path d="M5 6c3-2 11-2 14 0l-1.5 5-2.5-1.5v10H9V9.5L6.5 11Z" />
        </svg>
      );

    case "fullsleeve":
      return (
        <svg {...common}>
          <path d="M3 6c3-2 15-2 18 0l-2 7-2.5-2v11H9.5V11L7 13Z" />
        </svg>
      );

    case "patch":
      return (
        <svg {...common}>
          <rect x="5" y="5" width="14" height="14" rx="3" />
          <path d="M9 9h6v6H9z" />
        </svg>
      );

    case "download":
      return (
        <svg {...common}>
          <path d="M12 3v12" />
          <polyline points="7 11 12 16 17 11" />
          <path d="M4 19h16" />
        </svg>
      );

    case "bookmark":
      return (
        <svg {...common}>
          <path d="M6 3h12v18l-6-4-6 4Z" />
        </svg>
      );

    case "collar":
      return (
        <svg {...common}>
          <path d="M5 4h14l-3 6H8Z" />
          <circle cx="9" cy="8" r="0.6" fill="currentColor" />
          <circle cx="9" cy="12" r="0.6" fill="currentColor" />
          <circle cx="9" cy="16" r="0.6" fill="currentColor" />
        </svg>
      );

    case "pajama":
      return (
        <svg {...common}>
          <path d="M6 3h12l1 18h-4l-1-11-1 11H9L8 3Z" />
          <line x1="9" y1="7" x2="15" y2="7" />
        </svg>
      );

    case "cuff":
      return (
        <svg {...common}>
          <path d="M4 6c3-2 13-2 16 0l-2 6-3-2v10H9V10L6 12Z" />
          <circle cx="12" cy="10" r="0.8" fill="currentColor" />
        </svg>
      );

    case "fabric":
      return (
        <svg {...common}>
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <line x1="4" y1="10" x2="20" y2="10" />
          <line x1="4" y1="15" x2="20" y2="15" />
        </svg>
      );

    default:
      return null;
  }
};

/* ---------------------------------------------------------
   Icon mapping helper
--------------------------------------------------------- */

const NECK_ICON_MAP = {
  "v-neck": "vneck",
  angrakha: "crown",
  round: "round",
};

const SLEEVE_ICON_MAP = {
  sleeveless: "sleeveless",
  "half sleeve": "halfsleeve",
  "full sleeve": "fullsleeve",
};

const TROUSER_ICON_MAP = {
  straight: "straight",
  salwar: "salwar",
  tulip: "tulip",
};

const getIconFor = (map, name, fallback) =>
  map[(name || "").toLowerCase()] || fallback;

/* ---------------------------------------------------------
   Product -> customization-type classification

   Admin customization subcategory mein jo bhi product add
   hoga, uska naam in keyword lists se match hoga.
--------------------------------------------------------- */

// Women (For Her) keywords
const WOMEN_NECK_KEYWORDS = ["neck", "v-neck", "vneck", "angrakha", "collar"];
const WOMEN_SLEEVE_KEYWORDS = ["sleeve", "sleeveless"];
const WOMEN_TROUSER_KEYWORDS = [
  "trouser",
  "salwar",
  "shalwar",
  "tulip",
  "pant",
  "straight pant",
];
const LACE_KEYWORDS = ["lace"];
const PATCH_KEYWORDS = ["patch"];

// Men (For Him) keywords
const MEN_COLLAR_KEYWORDS = ["collar", "ban collar"];
const MEN_CUFF_KEYWORDS = ["cuff", "sleeveless"];
const MEN_PAJAMA_KEYWORDS = ["pajma", "pajama", "pyjama", "shalwar", "salwar"];
const MEN_FABRIC_KEYWORDS = ["fabric"];

const nameHasAny = (name, keywords) => {
  const lower = (name || "").toLowerCase();
  return keywords.some((k) => lower.includes(k));
};

/* ---------------------------------------------------------
   Swatch tile
--------------------------------------------------------- */

const SwatchTile = ({ gradient, label, active, onClick }) => (
  <Button
    variant="link"
    className={`swatch-tile p-0 ${
      active ? "swatch-tile--active" : ""
    }`}
    onClick={onClick}
  >
    <span
      className="swatch-tile__preview"
      style={{ background: gradient }}
    />
    <span className="swatch-tile__label">{label}</span>
  </Button>
);

/* ---------------------------------------------------------
   Option tile
--------------------------------------------------------- */

const OptionTile = ({ icon, label, imageUrl, active, onClick }) => (
  <Button
    variant="light"
    className={`option-tile ${
      active ? "option-tile--active" : ""
    }`}
    onClick={onClick}
  >
    {imageUrl ? (
      <span className="option-tile__image">
        <img
          src={imageUrl}
          alt={label}
          style={{
            width: "100%",
            height: 64,
            objectFit: "cover",
            borderRadius: 8,
            display: "block",
          }}
          onError={(e) => {
            console.error("Option image failed:", imageUrl);
            e.currentTarget.style.display = "none";
          }}
        />
      </span>
    ) : (
      <span className="option-tile__icon">
        <Icon name={icon} size={20} />
      </span>
    )}

    <span className="option-tile__label">{label}</span>
  </Button>
);

const DEFAULT_GRADIENT =
  "linear-gradient(160deg,#e9e5da,#cfc9bb)";

function Customization() {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    baseUrl,
    baseUrlForFiles,
    customizeImage,
  } = useContext(AppContext);

  /* ---------------------------------------------------------
     Agar user login page se wapis is page par bheja gaya hai
     (login ke baad), to state mein pending selection restore
     kar dete hain. Warna, refresh ke case mein sessionStorage
     se persisted state utha lete hain taake sab ghaib na ho.
  --------------------------------------------------------- */

  const persistedState = loadPersistedState();

  const restoredState = location.state?.resumeCustomization
    ? location.state.resumeCustomization
    : persistedState;

  /* ---------------------------------------------------------
     Product image
  --------------------------------------------------------- */

  const contextProductImage =
    customizeImage?.images?.[0]?.image_path
      ? buildFileUrl(
          customizeImage.images[0].image_path,
          baseUrlForFiles
        )
      : customizeImage?.image || null;

  const rawProductImage =
    location.state?.productImage ||
    restoredState?.productImage ||
    contextProductImage;

  const productImage = rawProductImage
    ? buildFileUrl(rawProductImage, baseUrlForFiles)
    : null;

  const productName =
    location.state?.productName ||
    restoredState?.productName ||
    customizeImage?.name ||
    "Modern Heritage Ensemble";

  /* ---------------------------------------------------------
     Debug: productImage na milne ki wajah console mein
     dikhado, taake pata chal jaye k navigate() state se
     image nahi aa rahi ya AppContext se bhi nahi mil rahi.
  --------------------------------------------------------- */

  useEffect(() => {
    if (!productImage) {
      console.warn(
        "Customization: productImage nahi mili.",
        {
          "location.state?.productImage":
            location.state?.productImage,
          "customizeImage (AppContext)": customizeImage,
        }
      );
    }
  }, [productImage, location.state, customizeImage]);

  /* ---------------------------------------------------------
     Customization subcategory
  --------------------------------------------------------- */

  // ── FIX (CONFIRMED via phpMyAdmin query on mlproducts):
  // mlsubcategories mein "For Her"/"For Him" ke duplicate
  // rows thay (14 vs 18, 16 vs 25), lekin actual products
  // ("neck design", "trouser design", "full sleeveless",
  // "straight trouser design", "patches", "V shape design")
  // sab mlsubcategory_id = 18 ke against save hain.
  // id 14 ke against sirf perfume products hain (unrelated).
  // Men ke customization products (ban collar/pajama/cuff/
  // fabric) mlsubcategory_id = 25 ke against hain.
  const FOR_HER_SUBCATEGORY_ID = 18;
  const FOR_HIM_SUBCATEGORY_ID = 25;

  // ProductCard se selected dress ki main category aati hai.
  // Women = category 1, Men = category 2.
  const categoryId = Number(
    location.state?.categoryId ?? restoredState?.categoryId
  );

  const isMen = categoryId === 2;

  const subcategoryId = isMen
    ? FOR_HIM_SUBCATEGORY_ID
    : FOR_HER_SUBCATEGORY_ID;

  /* ---------------------------------------------------------
     Section labels — men aur women k liye alag titles
  --------------------------------------------------------- */

  const sectionLabels = isMen
    ? {
        first: "Collar Design",
        second: "Cuff Design",
        third: "Pajama Design",
        fourth: "Fabric Design",
        firstEmpty: "Koi collar design available nahi.",
        secondEmpty: "Koi cuff design available nahi.",
        thirdEmpty: "Koi pajama design available nahi.",
        fourthEmpty: "Koi fabric design available nahi.",
      }
    : {
        first: "Neck Design",
        second: "Sleeve Style",
        third: "Trouser Style",
        firstEmpty: "Koi neck design available nahi.",
        secondEmpty: "Koi sleeve style available nahi.",
        thirdEmpty: "Koi trouser style available nahi.",
      };

  const [neckDesign, setNeckDesign] = useState(
    restoredState?.neckDesign || ""
  );
  const [clothStyle, setClothStyle] = useState(
    restoredState?.clothStyle || "Frock"
  );
  const [trouserStyle, setTrouserStyle] = useState(
    restoredState?.trouserStyle || ""
  );
  const [sleeveStyle, setSleeveStyle] = useState(
    restoredState?.sleeveStyle || ""
  );
  const [fabricStyle, setFabricStyle] = useState(
    restoredState?.fabricStyle || ""
  );

  const [embellishments, setEmbellishments] = useState(
    restoredState?.embellishments || []
  );

  const [prompt, setPrompt] = useState(
    restoredState?.prompt || ""
  );

  const [showLaceModal, setShowLaceModal] =
    useState(false);

  const [selectedLace, setSelectedLace] =
    useState(restoredState?.selectedLace || null);

  const [showPatchModal, setShowPatchModal] =
    useState(false);

  const [selectedPatch, setSelectedPatch] =
    useState(restoredState?.selectedPatch || null);

  /* ---------------------------------------------------------
     Generation state
     ── FIX: generatedImage ab persisted state se bhi restore
     hoti hai, taake refresh (F5) ke baad bhi result gayab na ho.
  --------------------------------------------------------- */

  const [isGenerating, setIsGenerating] =
    useState(false);

  const [generatedImage, setGeneratedImage] =
    useState(restoredState?.generatedImage || null);

  const [generationError, setGenerationError] =
    useState(null);

  /* ---------------------------------------------------------
     Save / Download state
  --------------------------------------------------------- */

  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(
    restoredState?.isSaved || false
  );
  const [saveMsg, setSaveMsg] = useState(null);

  /* ---------------------------------------------------------
     Trial limit state (2 tries / 72 hours)
  --------------------------------------------------------- */

  const [trialBlocked, setTrialBlocked] = useState(false);
const [trialWaitText, setTrialWaitText] = useState("");
const [remainingTrials, setRemainingTrials] = useState(2);

const fetchTrialStatus = async () => {
  const token = localStorage.getItem("authToken");
  if (!token) return;

  try {
    const res = await axios.get(`${baseUrl}/customization/trial-status`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (res.data.success) {
      setRemainingTrials(res.data.remaining);
      setTrialBlocked(res.data.blocked);
      setTrialWaitText(res.data.wait_text || "");
    }
  } catch (err) {
    console.error("Trial status error:", err);
  }
};

useEffect(() => {
  fetchTrialStatus();
}, []);

  /* ---------------------------------------------------------
     Backend customization options
     Neck / Sleeve / Trouser
  --------------------------------------------------------- */

  const [allOptions, setAllOptions] =
    useState([]);

  const [optionsLoading, setOptionsLoading] =
    useState(false);

  const [optionsError, setOptionsError] =
    useState(null);

  /* ---------------------------------------------------------
     Product based embellishments / options
  --------------------------------------------------------- */

  const [embellishmentProducts, setEmbellishmentProducts] =
    useState([]);

  const [embellishmentProductsLoading, setEmbellishmentProductsLoading] =
    useState(false);

  /* ---------------------------------------------------------
     Fetch customization options
     Neck / Sleeve / Trouser
  --------------------------------------------------------- */

  useEffect(() => {
    if (!subcategoryId) return;

    const fetchOptions = async () => {
      setOptionsLoading(true);
      setOptionsError(null);

      try {
        const res = await axios.post(
          `${baseUrl}/customer/customization-options/get`,
          {
            mlsubcategory_id: subcategoryId,
          }
        );

        if (res.data.success) {
          setAllOptions(res.data.data || []);
        } else {
          setOptionsError(
            "Customization options load nahi ho sakin."
          );
        }
      } catch (err) {
        console.error(
          "Customization options error:",
          err
        );

        setOptionsError(
          "Customization options load nahi ho sakin."
        );
      } finally {
        setOptionsLoading(false);
      }
    };

    fetchOptions();
  }, [subcategoryId, baseUrl]);

  /* ---------------------------------------------------------
     Fetch normal products for the selected gender's
     accessories subcategory (For Her = 14, For Him = 16)
  --------------------------------------------------------- */

  useEffect(() => {
    const fetchEmbellishmentProducts = async () => {
      setEmbellishmentProductsLoading(true);

      try {
        const res = await axios.post(
          `${baseUrl}/customer/products/loadAllProductForCustomer`,
          {
            subcategory_id: subcategoryId,
          }
        );

        setEmbellishmentProducts(
          res.data.products || []
        );
      } catch (err) {
        console.error(
          "Embellishment products error:",
          err
        );

        setEmbellishmentProducts([]);
      } finally {
        setEmbellishmentProductsLoading(false);
      }
    };

    fetchEmbellishmentProducts();
  }, [baseUrl, subcategoryId]);

  /* ---------------------------------------------------------
     Group customization_options table entries
  --------------------------------------------------------- */

  const neckOptionsFromTable = !isMen
    ? allOptions.filter((o) => o.option_type === "neck")
    : [];

  const sleeveOptionsFromTable = !isMen
    ? allOptions.filter((o) => o.option_type === "sleeve")
    : [];

  const trouserOptionsFromTable = !isMen
    ? allOptions.filter((o) => o.option_type === "trouser")
    : [];

  /* ---------------------------------------------------------
     Classify selected-gender customization PRODUCTS by name
     keywords.
  --------------------------------------------------------- */

  const neckProducts = embellishmentProducts.filter((p) =>
    nameHasAny(p.name, isMen ? MEN_COLLAR_KEYWORDS : WOMEN_NECK_KEYWORDS)
  );

  const sleeveProducts = embellishmentProducts.filter((p) =>
    nameHasAny(p.name, isMen ? MEN_CUFF_KEYWORDS : WOMEN_SLEEVE_KEYWORDS)
  );

  const trouserProducts = embellishmentProducts.filter((p) =>
    nameHasAny(p.name, isMen ? MEN_PAJAMA_KEYWORDS : WOMEN_TROUSER_KEYWORDS)
  );

  const fabricProducts = isMen
    ? embellishmentProducts.filter((p) =>
        nameHasAny(p.name, MEN_FABRIC_KEYWORDS)
      )
    : [];

  const laceOptions = !isMen
    ? embellishmentProducts.filter((p) => nameHasAny(p.name, LACE_KEYWORDS))
    : [];

  const patchOptions = !isMen
    ? embellishmentProducts.filter((p) => nameHasAny(p.name, PATCH_KEYWORDS))
    : [];

  /* ---------------------------------------------------------
     Final combined lists
  --------------------------------------------------------- */

  const neckOptions = [...neckOptionsFromTable, ...neckProducts];
  const sleeveOptions = [...sleeveOptionsFromTable, ...sleeveProducts];
  const trouserOptions = [...trouserOptionsFromTable, ...trouserProducts];
  const fabricOptions = fabricProducts;

  /* ---------------------------------------------------------
     IMPORTANT IMAGE URL FUNCTION
  --------------------------------------------------------- */

  function buildFileUrl(rawPath, filesBaseUrl) {
    if (!rawPath) return null;

    let path = String(rawPath).trim();

    if (!path) return null;

    /* Already complete URL */
    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    /* Remove starting slash */
    path = path.replace(/^\/+/, "");

    if (path.toLowerCase().startsWith("storage/")) {
      path = path.substring("storage/".length);
    }

    if (
      path
        .toLowerCase()
        .startsWith("public/storage/")
    ) {
      path = path.substring(
        "public/storage/".length
      );
    }

    return `${
      filesBaseUrl.replace(/\/+$/, "")
    }/${path}`;
  }

  /* ---------------------------------------------------------
     Product / customization option image URL
  --------------------------------------------------------- */

  const optionImageUrl = (opt) => {
    const rawPath =
      opt?.images?.[0]?.image_path ||
      opt?.image_path ||
      opt?.image ||
      opt?.path;

    return buildFileUrl(
      rawPath,
      baseUrlForFiles
    );
  };

  /* ---------------------------------------------------------
     Default options
  --------------------------------------------------------- */

  useEffect(() => {
    if (neckOptions.length && !neckDesign) {
      setNeckDesign(neckOptions[0].id);
    }

    if (sleeveOptions.length && !sleeveStyle) {
      setSleeveStyle(sleeveOptions[0].id);
    }

    if (trouserOptions.length && !trouserStyle) {
      setTrouserStyle(trouserOptions[0].id);
    }

    if (isMen && fabricOptions.length && !fabricStyle) {
      setFabricStyle(fabricOptions[0].id);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allOptions, embellishmentProducts]);

  /* ---------------------------------------------------------
     Trouser selection -> confirm toast
  --------------------------------------------------------- */

  const handleTrouserSelect = (opt) => {
    setTrouserStyle(opt.id);

    Swal.fire({
      icon: "success",
      title: "Selected",
      text: `${opt.name} laga diya hai.`,
      timer: 1400,
      showConfirmButton: false,
    });
  };

  /* ---------------------------------------------------------
     Auth helper — token check
  --------------------------------------------------------- */

  const isLoggedIn = () => {
    const token = localStorage.getItem("authToken");
    return Boolean(token);
  };

  /* ---------------------------------------------------------
     Login page par bhejna, saath mein current selection aur
     "wapis kahan aana hai" ka data bhi bhej dena taake login
     hone ke baad user seedha isi page par, isi selection ke
     saath wapis aa jaye.
  --------------------------------------------------------- */

  const redirectToLogin = () => {
    Swal.fire({
      icon: "info",
      title: "Login required",
      text: "Design generate karne ke liye pehle login karein.",
      timer: 1800,
      showConfirmButton: false,
    });

    navigate("/login", {
      state: {
        from: "/customization",
        // Login page ko batane ke liye k login ke baad
        // kahan navigate karna hai aur kya state bhejni hai.
        redirectTo: "/customization",
        resumeCustomization: {
          productImage,
          productName,
          categoryId,
          neckDesign,
          sleeveStyle,
          trouserStyle,
          fabricStyle,
          embellishments,
          selectedLace,
          selectedPatch,
          prompt,
          clothStyle,
          generatedImage,
          isSaved,
          // Generate ko login ke turant baad khud-ba-khud
          // trigger karne ke liye flag.
          autoGenerate: true,
        },
      },
    });
  };

  /* ---------------------------------------------------------
     Generate Design
  --------------------------------------------------------- */

  const handleGenerate = async () => {
    if (isGenerating) return;

    // ── LOGIN CHECK ──
    // Generate se pehle check karo user login hai ya nahi.
    // Agar nahi, to login page par bhej do (selection ke
    // saath) aur yahan se return kar do.
    if (!isLoggedIn()) {
      redirectToLogin();
      return;
    }

    if (!productImage) {
      setGenerationError(
        "Selected product ki image nahi mili. Pehle product image select karein."
      );
      return;
    }

    setIsGenerating(true);
    setGenerationError(null);
    setIsSaved(false);
    setSaveMsg(null);

    try {
      const formData = new FormData();

      const baseImageUrl = productImage;

     formData.append(
  "cloth_style",
  isMen
    ? "Men Unstitched Shalwar Kameez"
    : (clothStyle || "Frock")
);

formData.append("gender", isMen ? "men" : "women");
formData.append("is_men", isMen ? "1" : "0");

      const sameOptionId = (option, selectedId) =>
        String(option?.id ?? "") === String(selectedId ?? "");

      const selectedNeckOption = neckOptions.find((o) =>
        sameOptionId(o, neckDesign)
      );

      const selectedSleeveOption = sleeveOptions.find((o) =>
        sameOptionId(o, sleeveStyle)
      );

      const selectedTrouserOption = trouserOptions.find((o) =>
        sameOptionId(o, trouserStyle)
      );

      const selectedFabricOption = isMen
        ? fabricOptions.find((o) => sameOptionId(o, fabricStyle))
        : null;

      formData.append(
        "trouser_style",
        selectedTrouserOption?.name || ""
      );

      formData.append(
        "neckline",
        selectedNeckOption?.name || ""
      );

      formData.append(
        "sleeves",
        selectedSleeveOption?.name || ""
      );
      const sleeveImgUrl = selectedSleeveOption
  ? optionImageUrl(selectedSleeveOption)
  : null;

if (sleeveImgUrl) {
  formData.append(
    "custom_sleeve_image_url",
    sleeveImgUrl
  );
}

      if (isMen) {
        formData.append(
          "fabric_style",
          selectedFabricOption?.name || ""
        );
      }

      formData.append("preserve_base_garment", "1");
      formData.append("reference_mode", "edit");

      embellishments.forEach((item) => {
        if (item) {
          formData.append("embellishments[]", item);
        }
      });

      if (prompt.trim()) {
        formData.append(
          "extra_instructions",
          prompt.trim()
        );
      }

      formData.append(
        "custom_silhouette_image_url",
        baseImageUrl
      );

      formData.append(
        "custom_cloth_image_url",
        baseImageUrl
      );

      formData.append(
        "base_image_url",
        baseImageUrl
      );

      const neckImgUrl = selectedNeckOption
        ? optionImageUrl(selectedNeckOption)
        : null;

      if (neckImgUrl) {
        formData.append(
          "custom_neck_image_url",
          neckImgUrl
        );
      }

      const trouserImgUrl = selectedTrouserOption
        ? optionImageUrl(selectedTrouserOption)
        : null;

      if (trouserImgUrl) {
        formData.append(
          "custom_trouser_image_url",
          trouserImgUrl
        );
      }

      if (isMen) {
        const fabricImgUrl = selectedFabricOption
          ? optionImageUrl(selectedFabricOption)
          : null;

        if (fabricImgUrl) {
          formData.append(
            "custom_fabric_image_url",
            fabricImgUrl
          );
        }
      }

      const laceImgUrl = selectedLace
        ? optionImageUrl(selectedLace)
        : null;

      const patchImgUrl = selectedPatch
        ? optionImageUrl(selectedPatch)
        : null;

      const embellishmentUrls = [
        laceImgUrl,
        patchImgUrl,
      ].filter(Boolean);

      embellishmentUrls.forEach((url) => {
        formData.append(
          "custom_embellishment_image_urls[]",
          url
        );
      });

      if (embellishmentUrls[0]) {
        formData.append(
          "custom_embellishment_image_url",
          encodeURI(embellishmentUrls[0])
        );
      }

      const token = localStorage.getItem("authToken");

      console.log("AI Generation Payload:", {
        productName,
        baseImageUrl,
        clothStyle,
        trouserStyle: selectedTrouserOption?.name || null,
        neckDesign: selectedNeckOption?.name || null,
        sleeveStyle: selectedSleeveOption?.name || null,
        fabricStyle: selectedFabricOption?.name || null,
        embellishments,
        lace: laceImgUrl,
        patch: patchImgUrl,
        prompt: prompt.trim(),
      });

      const res = await axios.post(
        `${baseUrl}/customization/generate`,
        formData,
        {
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
           timeout: 300000,
        }
      );

      if (res.data?.success) {
        const returnedImage =
          res.data?.data?.image_url ||
          res.data?.data?.url ||
          res.data?.data;

        if (!returnedImage) {
          throw new Error(
            "Backend ne generated image URL return nahi ki."
          );
        }

        const finalImageUrl = buildFileUrl(
          returnedImage,
          baseUrlForFiles
        );

        setGeneratedImage(finalImageUrl);

       fetchTrialStatus();
      } else {
        setGenerationError(
          res.data?.error ||
            res.data?.message ||
            "Design generate nahi ho saka."
        );
      }
    } catch (err) {
      console.error("Generate design error:", err);

      const backendMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message;

      // Agar backend bhi 401 (unauthenticated) return kare,
      // to bhi login page par bhej do.
      if (err.response?.status === 401) {
        redirectToLogin();
        setIsGenerating(false);
        return;
      }

      setGenerationError(
        backendMsg ||
          "Something went wrong. Please try again."
      );

      if (err.response?.status === 429) {
        Swal.fire({
          icon: "info",
          title: "Limit reached",
          text:
            backendMsg ||
            "You've reached today's generation limit.",
        });
         fetchTrialStatus(); 
      }
    } finally {
      setIsGenerating(false);
    }
  };

  /* ---------------------------------------------------------
     Login ke baad wapis is page par aane par, agar
     autoGenerate flag set tha, to Generate khud-ba-khud
     trigger kar do (sirf ek dafa).
  --------------------------------------------------------- */

  const autoGenerateStartedRef = React.useRef(false);

  useEffect(() => {
    if (autoGenerateStartedRef.current) return;

    const ready =
      restoredState?.autoGenerate &&
      isLoggedIn() &&
      Boolean(productImage) &&
      !optionsLoading &&
      !embellishmentProductsLoading &&
      (neckOptions.length === 0 || Boolean(neckDesign)) &&
      (sleeveOptions.length === 0 || Boolean(sleeveStyle)) &&
      (trouserOptions.length === 0 || Boolean(trouserStyle)) &&
      (!isMen || fabricOptions.length === 0 || Boolean(fabricStyle));

    if (!ready) return;

    autoGenerateStartedRef.current = true;

    // History state se flag hata dete hain taake refresh
    // pe dobara generate na ho jaye.
    window.history.replaceState({}, document.title);
    handleGenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    restoredState?.autoGenerate,
    productImage,
    optionsLoading,
    embellishmentProductsLoading,
    neckOptions.length,
    sleeveOptions.length,
    trouserOptions.length,
    fabricOptions.length,
    neckDesign,
    sleeveStyle,
    trouserStyle,
    fabricStyle,
  ]);

  /* ---------------------------------------------------------
     ── FIX: Refresh (F5) ke baad bhi selection + generated
     image gayab na ho, isliye har relevant state change par
     sessionStorage mein state save kar dete hain.
  --------------------------------------------------------- */

  useEffect(() => {
    savePersistedState({
      productImage,
      productName,
      categoryId,
      neckDesign,
      sleeveStyle,
      trouserStyle,
      fabricStyle,
      embellishments,
      selectedLace,
      selectedPatch,
      prompt,
      clothStyle,
      generatedImage,
      isSaved,
    });
  }, [
    productImage,
    productName,
    categoryId,
    neckDesign,
    sleeveStyle,
    trouserStyle,
    fabricStyle,
    embellishments,
    selectedLace,
    selectedPatch,
    prompt,
    clothStyle,
    generatedImage,
    isSaved,
  ]);

  /* ---------------------------------------------------------
     Download generated design to device
  --------------------------------------------------------- */

  const handleDownloadResult = () => {
    if (!generatedImage) return;
    downloadImageFromUrl(
      generatedImage,
      `customization_${productName.replace(/\s+/g, "_")}`
    );
  };

  /* ---------------------------------------------------------
     Save generated design to user's account (backend)
  --------------------------------------------------------- */

  const handleSaveResult = async () => {
    if (!generatedImage || isSaving || isSaved) return;

    if (!isLoggedIn()) {
      redirectToLogin();
      return;
    }

    setIsSaving(true);
    setSaveMsg(null);

    try {
      const token = localStorage.getItem("authToken");

      const res = await axios.post(
        `${baseUrl}/customer/saved-designs`,
        {
          type: "customization",
          image_path: generatedImage,
          title: productName,
          product_id: customizeImage?.id || null,
        },
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (res.data.success) {
        setIsSaved(true);
        setSaveMsg("Saved to your account ✓");
      } else {
        setSaveMsg(res.data.error || "Save nahi ho saka.");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        redirectToLogin();
        return;
      }

      setSaveMsg(
        error.response?.data?.error || "Save karte waqt masla hua."
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="aura">

      {/* Back button */}

      <div className="aura-back-row">
        <Button
          className="back-btn back-btn--arrow"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <Icon name="back" size={20} />
        </Button>
      </div>

      {/* Intro */}

      <Container fluid className="aura-intro">
        <p className="aura-intro__eyebrow">
          Customize Your Outfit
        </p>

        <p className="aura-intro__title">
         Create your perfect outfit.
        </p>
      </Container>

      {optionsError && (
        <Container fluid>
          <p className="text-danger text-center mb-0">
            {optionsError}
          </p>
        </Container>
      )}

      {trialBlocked && (
        <Container fluid>
          <p className="text-center mb-0" style={{ fontSize: 13, color: "#a15c00" }}>
            ⏳ {trialWaitText}
          </p>
        </Container>
      )}

      {/* Main grid */}

      <Container fluid className="aura-grid">
        <Row className="gy-4">

          {/* Left column */}

          <Col
            xs={12}
            lg={3}
            className="aura-col aura-col--left d-flex flex-column gap-3"
          >

            {productImage && (
              <Card className="panel">
                <Card.Body className="d-flex align-items-center gap-3">

                  <img
                    src={productImage}
                    alt={productName}
                    style={{
                      width: 56,
                      height: 56,
                      objectFit: "cover",
                      borderRadius: 8,
                      flexShrink: 0,
                    }}
                    onError={(e) => {
                      console.error(
                        "Product image failed:",
                        productImage
                      );
                    }}
                  />

                  <div>
                    <p className="panel__eyebrow mb-0">
                      Selected Product
                    </p>

                    <p className="mb-0 fw-semibold">
                      {productName}
                    </p>

                    <p
                      className="mb-0 text-success d-flex align-items-center gap-1"
                      style={{ fontSize: 13 }}
                    >
                      <Icon
                        name="check"
                        size={13}
                      />

                      Base item selected
                    </p>
                  </div>

                </Card.Body>
              </Card>
            )}

            {/* Neck Design / Collar Design */}

            <Card className="panel">
              <Card.Body>

                <Card.Title className="panel__section-title">
                  {sectionLabels.first}
                </Card.Title>

                {optionsLoading || embellishmentProductsLoading ? (
                  <Spinner
                    animation="border"
                    size="sm"
                  />
                ) : neckOptions.length === 0 ? (
                  <p
                    className="text-muted mb-0"
                    style={{ fontSize: 13 }}
                  >
                    {sectionLabels.firstEmpty}
                  </p>
                ) : (
                  <Row className="g-2">

                    {neckOptions.map((opt) => (
                      <Col
                        xs={6}
                        key={opt.id}
                      >
                        <OptionTile
                          icon={
                            isMen
                              ? "collar"
                              : getIconFor(
                                  NECK_ICON_MAP,
                                  opt.name,
                                  "neckline"
                                )
                          }
                          imageUrl={optionImageUrl(opt)}
                          label={opt.name}
                          active={
                            neckDesign ===
                            opt.id
                          }
                          onClick={() =>
                            setNeckDesign(
                              opt.id
                            )
                          }
                        />
                      </Col>
                    ))}

                  </Row>
                )}

              </Card.Body>
            </Card>

            {/* Sleeve Style / Cuff Design */}

            <Card className="panel">
              <Card.Body>

                <Card.Title className="panel__section-title">
                  {sectionLabels.second}
                </Card.Title>

                {optionsLoading || embellishmentProductsLoading ? (
                  <Spinner
                    animation="border"
                    size="sm"
                  />
                ) : sleeveOptions.length === 0 ? (
                  <p
                    className="text-muted mb-0"
                    style={{ fontSize: 13 }}
                  >
                    {sectionLabels.secondEmpty}
                  </p>
                ) : (
                  <Row className="g-2">

                    {sleeveOptions.map((opt) => (
                      <Col
                        xs={6}
                        key={opt.id}
                      >
                        <OptionTile
                          icon={
                            isMen
                              ? "cuff"
                              : getIconFor(
                                  SLEEVE_ICON_MAP,
                                  opt.name,
                                  "sleeve"
                                )
                          }
                          imageUrl={optionImageUrl(opt)}
                          label={opt.name}
                          active={
                            sleeveStyle ===
                            opt.id
                          }
                          onClick={() =>
                            setSleeveStyle(
                              opt.id
                            )
                          }
                        />
                      </Col>
                    ))}

                  </Row>
                )}

              </Card.Body>
            </Card>

            <Button
              variant="outline-dark"
              className="save-draft"
            >
              Save Draft
            </Button>

          </Col>

          {/* Center preview */}

          <Col
            xs={12}
            lg={6}
            className="aura-col aura-col--center"
          >

            <Card className="preview-card border-0">

              {isGenerating ? (
                <div
                  className="preview-card__image d-flex align-items-center justify-content-center"
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: 320,
                  }}
                >
                  <div className="text-center">

                    <Spinner
                      animation="border"
                      role="status"
                      className="mb-2"
                    />

                    <p className="mb-0">
                      Generating your design...
                    </p>

                  </div>
                </div>
              ) : generatedImage ||
                productImage ? (

                <img
                  src={
                    generatedImage ||
                    productImage
                  }
                  alt={productName}
                  className="preview-card__image"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                  onError={(e) => {
                    console.error(
                      "Preview image failed:",
                      generatedImage ||
                        productImage
                    );
                  }}
                />

              ) : (

                <div
                  className="preview-card__image d-flex align-items-center justify-content-center"
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: 320,
                  }}
                >
                  <p
                    className="text-center text-muted mb-0 px-3"
                    style={{ fontSize: 14 }}
                  >
                    Product image nahi mili. Product page se "Customize"
                    button pe dobara click karein, ya console check karein.
                  </p>
                </div>

              )}

              <Card.ImgOverlay className="preview-card__overlay">

                <div className="preview-card__caption">

                  <p className="preview-card__eyebrow">
                    {generatedImage
                      ? "Generated Design"
                      : "Current Preview"}
                  </p>

                  <Card.Title className="preview-card__title">
                    {productName}
                  </Card.Title>

                  <p className="preview-card__meta d-flex align-items-center gap-1 mb-0">

                    <Icon
                      name="check"
                      size={13}
                    />

                    AI Rendered in Real-time
                  </p>

                </div>

              </Card.ImgOverlay>

            </Card>

            {generationError && (
              <p className="text-danger mt-2 mb-0 text-center">
                {generationError}
              </p>
            )}

            {generatedImage && !isGenerating && (
              <>
                <div
                  className="result-action-buttons mt-3 d-flex gap-2 flex-wrap justify-content-center"
                >
                  <Button
                    variant="outline-dark"
                    className="d-flex align-items-center gap-2"
                    onClick={handleDownloadResult}
                  >
                    <Icon name="download" size={16} />
                    Download
                  </Button>

                  <Button
                    className="generate-btn d-flex align-items-center gap-2"
                    onClick={handleSaveResult}
                    disabled={isSaving || isSaved}
                  >
                    <Icon name="bookmark" size={16} />
                    {isSaved
                      ? "Saved"
                      : isSaving
                      ? "Saving..."
                      : "Save"}
                  </Button>
                </div>

                {saveMsg && (
                  <p className="text-center mt-2 mb-0" style={{ fontSize: 13 }}>
                    {saveMsg}
                  </p>
                )}
              </>
            )}

          </Col>

          {/* Right column */}

          <Col
            xs={12}
            lg={3}
            className="aura-col aura-col--right d-flex flex-column gap-3"
          >
            {/* Trouser Style / Pajama Design */}

            <Card className="panel">
              <Card.Body>

                <Card.Title className="panel__section-title">
                  {sectionLabels.third}
                </Card.Title>

                {optionsLoading || embellishmentProductsLoading ? (
                  <Spinner
                    animation="border"
                    size="sm"
                  />
                ) : trouserOptions.length === 0 ? (
                  <p
                    className="text-muted mb-0"
                    style={{ fontSize: 13 }}
                  >
                    {sectionLabels.thirdEmpty}
                  </p>
                ) : (
                  <Row className="g-2">

                    {trouserOptions.map((opt) => (
                      <Col
                        xs={4}
                        key={opt.id}
                      >
                        <OptionTile
                          icon={
                            isMen
                              ? getIconFor(
                                  {},
                                  opt.name,
                                  nameHasAny(opt.name, MEN_FABRIC_KEYWORDS)
                                    ? "fabric"
                                    : "pajama"
                                )
                              : getIconFor(
                                  TROUSER_ICON_MAP,
                                  opt.name,
                                  "trouser"
                                )
                          }
                          imageUrl={optionImageUrl(opt)}
                          label={opt.name}
                          active={
                            trouserStyle ===
                            opt.id
                          }
                          onClick={() =>
                            handleTrouserSelect(opt)
                          }
                        />
                      </Col>
                    ))}

                  </Row>
                )}

              </Card.Body>
            </Card>

            {isMen ? (
              <Card className="panel">
                <Card.Body>

                  <Card.Title className="panel__section-title">
                    {sectionLabels.fourth}
                  </Card.Title>

                  {embellishmentProductsLoading ? (
                    <Spinner
                      animation="border"
                      size="sm"
                    />
                  ) : fabricOptions.length === 0 ? (
                    <p
                      className="text-muted mb-0"
                      style={{ fontSize: 13 }}
                    >
                      {sectionLabels.fourthEmpty}
                    </p>
                  ) : (
                    <Row className="g-2">

                      {fabricOptions.map((opt) => (
                        <Col
                          xs={6}
                          key={opt.id}
                        >
                          <OptionTile
                            icon="fabric"
                            imageUrl={optionImageUrl(opt)}
                            label={opt.name}
                            active={
                              fabricStyle ===
                              opt.id
                            }
                            onClick={() =>
                              setFabricStyle(
                                opt.id
                              )
                            }
                          />
                        </Col>
                      ))}

                    </Row>
                  )}

                </Card.Body>
              </Card>
            ) : (
              <Card className="panel">
                <Card.Body>

                  <Card.Title className="panel__section-title">
                    Embellishments
                  </Card.Title>

                  <ButtonGroup className="chip-row flex-wrap gap-2">

                    {/* Lace */}

                    <Button
                      variant="light"
                      className={`chip d-flex align-items-center gap-2 ${
                        embellishments.includes(
                          "Lace"
                        )
                          ? "chip--active"
                          : ""
                      }`}
                      onClick={() =>
                        setShowLaceModal(true)
                      }
                      disabled={
                        embellishmentProductsLoading ||
                        laceOptions.length === 0
                      }
                    >
                      <span
                        className="chip__swatch"
                        style={{
                          background:
                            DEFAULT_GRADIENT,
                        }}
                      />

                      {selectedLace
                        ? selectedLace.name
                        : "Lace"}
                    </Button>

                    {/* Patch */}

                    <Button
                      variant="light"
                      className={`chip d-flex align-items-center gap-2 ${
                        embellishments.includes(
                          "Patch"
                        )
                          ? "chip--active"
                          : ""
                      }`}
                      onClick={() =>
                        setShowPatchModal(true)
                      }
                      disabled={
                        embellishmentProductsLoading ||
                        patchOptions.length === 0
                      }
                    >
                      <span
                        className="chip__swatch"
                        style={{
                          background:
                            DEFAULT_GRADIENT,
                        }}
                      />

                      {selectedPatch
                        ? selectedPatch.name
                        : "Patches"}
                    </Button>

                  </ButtonGroup>

                </Card.Body>
              </Card>
            )}

            {/* AI Designer */}

            <Card className="ai-designer">
              <Card.Body>

                <div className="ai-designer__header d-flex align-items-center justify-content-between">

                  <span className="ai-designer__title d-flex align-items-center gap-2">

                    <Icon
                      name="sparkle"
                      size={16}
                    />

                    AI Designer

                  </span>

                  <span className="ai-designer__count">
                    {prompt.length} / 500
                  </span>

                </div>

                <Form.Control
                  as="textarea"
                  className="ai-designer__textarea"
                  placeholder="Describe specific modifications..."
                  maxLength={500}
                  value={prompt}
                  onChange={(e) =>
                    setPrompt(e.target.value)
                  }
                  rows={3}
                />

              </Card.Body>
            </Card>

            {/* Generate */}

            <Button
              className="generate-btn d-flex align-items-center justify-content-center gap-2"
              onClick={handleGenerate}
              disabled={isGenerating || trialBlocked}
            >

              {isGenerating ? (
                <Spinner
                  animation="border"
                  size="sm"
                />
              ) : (
                <Icon
                  name="sparkle"
                  size={17}
                />
              )}

              {isGenerating
                ? "Generating..."
                : "Generate Design"}

            </Button>

            {!trialBlocked && (
              <p className="generate-hint text-center mb-0">
                {remainingTrials} trial{remainingTrials === 1 ? "" : "s"} left · Estimated render time: 12 seconds
              </p>
            )}

          </Col>

        </Row>
      </Container>

      {/* ---------------------------------------------------------
          Lace selection modal
      --------------------------------------------------------- */}

      <Modal
        show={showLaceModal}
        onHide={() =>
          setShowLaceModal(false)
        }
        centered
        className="lace-modal"
      >

        <Modal.Header closeButton>

          <Modal.Title className="lace-modal__title">
            Choose Your Lace
          </Modal.Title>

        </Modal.Header>

        <Modal.Body>

          <Row className="g-3">

            {laceOptions.map((lace) => {

              const imageUrl =
                optionImageUrl(lace);

              return (
                <Col
                  xs={6}
                  key={lace.id}
                >

                  <button
                    type="button"
                    className={`lace-option ${
                      selectedLace?.id ===
                      lace.id
                        ? "lace-option--active"
                        : ""
                    }`}
                    onClick={() => {

                      setSelectedLace(lace);

                      setEmbellishments(
                        (prev) =>
                          prev.includes("Lace")
                            ? prev
                            : [
                                ...prev,
                                "Lace",
                              ]
                      );

                    }}
                  >

                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={lace.name}
                        className="lace-option__preview"
                        style={{
                          objectFit: "cover",
                        }}
                        onError={(e) => {

                          console.error(
                            "Lace image failed:",
                            imageUrl
                          );

                          e.currentTarget.style.display =
                            "none";
                        }}
                      />
                    ) : (
                      <span
                        className="lace-option__preview"
                        style={{
                          background:
                            DEFAULT_GRADIENT,
                        }}
                      />
                    )}

                    <span className="lace-option__name">
                      {lace.name}
                    </span>

                    <span className="lace-option__price">
                      {lace.base_price
                        ? `PKR ${lace.base_price}`
                        : ""}
                    </span>

                  </button>

                </Col>
              );
            })}

            {laceOptions.length === 0 && (
              <Col xs={12}>
                <p className="text-muted text-center mb-0">
                  Koi lace option available nahi.
                </p>
              </Col>
            )}

          </Row>

        </Modal.Body>

        <Modal.Footer>

          <Button
            variant="outline-dark"
            onClick={() =>
              setShowLaceModal(false)
            }
          >
            Cancel
          </Button>

          <Button
            className="generate-btn"
            disabled={!selectedLace}
            onClick={() =>
              setShowLaceModal(false)
            }
          >
            Confirm Selection
          </Button>

        </Modal.Footer>

      </Modal>

      {/* ---------------------------------------------------------
          Patch selection modal
      --------------------------------------------------------- */}

      <Modal
        show={showPatchModal}
        onHide={() =>
          setShowPatchModal(false)
        }
        centered
        className="lace-modal"
      >

        <Modal.Header closeButton>

          <Modal.Title className="lace-modal__title">
            Choose Your Patch
          </Modal.Title>

        </Modal.Header>

        <Modal.Body>

          <Row className="g-3">

            {patchOptions.map((patch) => {

              const imageUrl =
                optionImageUrl(patch);

              return (
                <Col
                  xs={6}
                  key={patch.id}
                >

                  <button
                    type="button"
                    className={`lace-option ${
                      selectedPatch?.id ===
                      patch.id
                        ? "lace-option--active"
                        : ""
                    }`}
                    onClick={() => {

                      setSelectedPatch(patch);

                      setEmbellishments(
                        (prev) =>
                          prev.includes("Patch")
                            ? prev
                            : [
                                ...prev,
                                "Patch",
                              ]
                      );

                    }}
                  >

                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={patch.name}
                        className="lace-option__preview"
                        style={{
                          objectFit: "cover",
                        }}
                        onError={(e) => {

                          console.error(
                            "Patch image failed:",
                            imageUrl
                          );

                          e.currentTarget.style.display =
                            "none";
                        }}
                      />
                    ) : (
                      <span
                        className="lace-option__preview"
                        style={{
                          background:
                            DEFAULT_GRADIENT,
                        }}
                      />
                    )}

                    <span className="lace-option__name">
                      {patch.name}
                    </span>

                    <span className="lace-option__price">
                      {patch.base_price
                        ? `PKR ${patch.base_price}`
                        : ""}
                    </span>

                  </button>

                </Col>
              );
            })}

            {patchOptions.length === 0 && (
              <Col xs={12}>
                <p className="text-muted text-center mb-0">
                  Koi patch option available nahi.
                </p>
              </Col>
            )}

          </Row>

        </Modal.Body>

        <Modal.Footer>

          <Button
            variant="outline-dark"
            onClick={() =>
              setShowPatchModal(false)
            }
          >
            Cancel
          </Button>

          <Button
            className="generate-btn"
            disabled={!selectedPatch}
            onClick={() =>
              setShowPatchModal(false)
            }
          >
            Confirm Selection
          </Button>

        </Modal.Footer>

      </Modal>

    </div>
  );
}

export default Customization;